import {
  ChecklistRtlRounded,
  DescriptionRounded,
  MailOutlineRounded,
  MarkEmailUnreadRounded,
  NoteAddRounded,
} from "@mui/icons-material";
import { ExecutionStep } from "../types/ExecutionJob/ExecutionStep.ts";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SvgIconTypeMap } from "@mui/material";
import { translate } from "./Translations/translatorService";
import { DrawerState } from "../types/DrawerState";
import { UpdateResourceParams } from "./ReactQuery/reactQueryService";
import { UseMutationResult } from "@tanstack/react-query";

export const getExecutionStepIcon = (actionType: ExecutionStep["actionType"]) => {
  const iconByActionType: Record<ExecutionStep["actionType"], OverridableComponent<SvgIconTypeMap>> = {
    createActivity: ChecklistRtlRounded,
    createInquiryToCustomer: MarkEmailUnreadRounded,
    sendCustomerMessage: MailOutlineRounded,
    sendEmail: MailOutlineRounded,
    sendBeaOrFax: MailOutlineRounded,
    createNote: NoteAddRounded,
    createDocument: DescriptionRounded,
  };

  return iconByActionType[actionType] || DescriptionRounded;
};

export const getExecutionStepDescription = (step: ExecutionStep): string => {
  const { actionType, payload } = step;

  switch (actionType) {
    case "sendCustomerMessage":
      return String(
        (typeof payload.conversation === "object" && payload.conversation?.subject) ||
          payload.subject ||
          "Kundennachricht vorbereitet",
      );
    case "sendEmail":
      return String(payload.toMail || payload.subject || "E-Mail vorbereitet");
    case "createActivity":
      return String(payload.subject || payload.type || "Aktivität vorbereitet");
    case "createInquiryToCustomer":
      return "Anfrage an Mandantschaft vorbereitet";
    case "createNote":
      return String(payload.content || "Notiz vorbereitet")
        .replace(/<[^>]+>/g, "")
        .slice(0, 120);
    case "sendBeaOrFax":
      return payload.communicationMedium === "bea"
        ? String(payload.bea?.subject || payload.bea?.externalReference || "beA-Nachricht/Fax vorbereitet")
        : String(payload.fax?.fax || "beA-Nachricht/Fax vorbereitet");
    case "createDocument":
      return String(payload.title || payload.documentType || "Dokument vorbereitet");
    default:
      return translate(`executionJob.step.actionType.values.${actionType}`);
  }
};

export const getExecutionStepEditDrawerConfig = (
  step: ExecutionStep,
  updateMutation: UseMutationResult<any, unknown, UpdateResourceParams>,
): DrawerState | null => {
  const getDrawerBaseData = <TPayload extends ExecutionStep["payload"]>(payload: TPayload) => ({
    // This initializes the drawer with the current step payload. Later AI changes replace entityData.
    entityData: payload,
    overwrittenHandleSave: async (draftPayload: ExecutionStep["payload"]) => {
      await updateMutation.mutateAsync({
        uri: "execution_steps",
        id: step.id,
        data: {
          // This marks user saves so syncStepEditDrawer does not reset the form during typing.
          payload: { ...draftPayload, lastEditedBy: "user" },
        },
      });
    },
    highlighted: true,
  });

  switch (step.actionType) {
    case "createActivity":
      return {
        type: "activity",
        ...getDrawerBaseData(step.payload),
      };
    case "createNote":
      return {
        type: "note",
        ...getDrawerBaseData(step.payload),
      };
    case "sendCustomerMessage":
      return {
        type: "message",
        typeSpecificData: {
          customerCommunicationOnly: true, //recipient is structurally fixed to the case customer by the backend sendCustomerMessage contract
        },
        ...getDrawerBaseData(step.payload),
      };
    case "sendBeaOrFax":
      return {
        type: "beaFax",
        ...getDrawerBaseData(step.payload),
      };
    case "createInquiryToCustomer":
      return {
        type: "inquiryToCustomer",
        ...getDrawerBaseData(step.payload),
      };
    case "createDocument":
      return {
        type: "documentEditor",
        wide: true,
        ...getDrawerBaseData(step.payload),
      };
    case "sendEmail":
      return {
        type: "email",
        ...getDrawerBaseData(step.payload),
      };
    default:
      return null;
  }
};
